import { createRoot, type Root } from 'react-dom/client';
import styled from 'styled-components';
import {
  Widget,
  type MapViewState,
  type Viewport,
  type WidgetPlacement,
  type WidgetProps,
} from '@deck.gl/core';
import { calculateMapScale } from '../../utils/map';
import { formatDistance } from '../../utils/format';

type ScaleBarWidgetProps = WidgetProps & {
  placement?: WidgetPlacement;
  viewId?: string | null;
  mapColor: 'light' | 'dark';
};

class ScaleBarWidget extends Widget<ScaleBarWidgetProps> {
  static defaultProps: Required<ScaleBarWidgetProps> = {
    ...Widget.defaultProps,
    id: 'scale-bar',
    placement: 'top-left',
    viewId: null,
    mapColor: 'light',
  };

  root: Root | null = null;
  className = 'deck-widget-scale-bar';
  placement: WidgetPlacement = 'top-left';
  viewStates: Record<string, MapViewState> = {};
  width: number = 0;
  distance: number = 0;

  constructor(props: ScaleBarWidgetProps) {
    super(props);
    this.setProps(this.props);
  }

  public setProps(props: Partial<ScaleBarWidgetProps>): void {
    super.setProps(props);
    this.placement = props.placement ?? this.placement;
    this.viewId = props.viewId ?? this.viewId;
  }

  public onAdd(): HTMLDivElement {
    const rootElement = this.onCreateRootElement();
    this.root = createRoot(rootElement);
    return rootElement;
  }

  public onRemove(): void {
    this.root?.unmount();
    this.root = null;
  }

  public onRenderHTML(): void {
    this.root?.render(
      <div className="deck-widget-scale-bar">
        <Bar barWidth={this.width} mapColor={this.props.mapColor}>
          {formatDistance(this.distance)}
        </Bar>
      </div>
    );
  }

  public onViewportChange(viewport: Viewport): void {
    const currentViewState = this.viewStates?.[viewport.id] ?? null;
    const nextViewState = this.getViewState(viewport.id);

    // 現在のビュー状態と新しいビュー状態が大きく変化した場合のみ更新する
    if (
      !currentViewState ||
      currentViewState.longitude.toFixed(6) !==
        nextViewState.longitude.toFixed(6) ||
      currentViewState.latitude.toFixed(6) !==
        nextViewState.latitude.toFixed(6) ||
      currentViewState.zoom.toFixed(2) !== nextViewState.zoom.toFixed(2)
    ) {
      this.viewStates = {
        ...this.viewStates,
        [viewport.id]: nextViewState,
      };

      // 基準幅(=120px)が地図上で何メートルに相当するかを再計算する
      const { distance, width } = this.calculateScaleBarWidth(
        nextViewState,
        120
      );
      this.distance = distance;
      this.width = width;

      this.updateHTML();
    }
  }

  protected getViewState(viewId: string): MapViewState {
    const viewState = super.getViewState(viewId);

    if (
      typeof viewState.latitude === 'number' &&
      typeof viewState.longitude === 'number' &&
      typeof viewState.zoom === 'number'
    ) {
      return {
        ...viewState,
        latitude: viewState.latitude,
        longitude: viewState.longitude,
        zoom: viewState.zoom,
      };
    }
    return {
      ...viewState,
      longitude: Number.NaN,
      latitude: Number.NaN,
      zoom: Number.NaN,
    };
  }
  private calculateScaleBarWidth(
    viewState: MapViewState,
    baseWidth: number
  ): { distance: number; width: number } {
    // 指定された基準幅(px)が地図上で何メートルに相当するかを計算する
    const scale =
      calculateMapScale(
        viewState.latitude,
        viewState.longitude,
        viewState.zoom
      ) * baseWidth;

    // 縮尺の目盛りの値として、10^N、2.5*10^N、5*10^N、10*10^N(m)の中から最も近いものを選択する
    const baseScale = Math.pow(10, Math.floor(Math.log10(scale)));
    const candidates = [
      baseScale,
      2.5 * baseScale,
      5 * baseScale,
      10 * baseScale,
    ];
    const distance = candidates.reduce(
      (selectedValue: number, value: number): number => {
        return Math.abs(value - scale) < Math.abs(selectedValue - scale)
          ? value
          : selectedValue;
      },
      Infinity
    );

    return { distance: distance, width: baseWidth * (distance / scale) };
  }
}

const Bar = styled.div<{ barWidth: number; mapColor: 'light' | 'dark' }>`
  width: ${(props) => `${props.barWidth}px`};
  height: 20px;
  line-height: 20px;
  padding: 0 5px;
  color: ${(props) => (props.mapColor === 'dark' ? '#FFFFFF' : '#333333')};
  background-color: ${(props) =>
    props.mapColor === 'dark' ? '#00000080' : '#FFFFFF80'};
  border-color: ${(props) =>
    props.mapColor === 'dark' ? '#FFFFFF' : '#333333'};
  border-width: medium 2px 2px;
  border-style: none solid solid;
  box-sizing: border-box;
  white-space: nowrap;
  text-align: center;
  font-size: 10px;
  font-weight: 500;
`;

export { ScaleBarWidget };
